import { useCallback, useEffect, useRef } from 'react'
import { NotifiableError } from '@bugsnag/js'
import { jwtDecode } from 'jwt-decode'
import { refreshAuthToken } from '@/services/api/ellisIslandClient'
import useSiteAssets from '@/services/hooks/useSiteAssets'
import { report } from '@/utils'
import { getPersistedAuth, setPersistedAuth } from './utils'

const useAuthTokenRefresher = ({
  enabled,
  timeout = 5000,
}: {
  enabled: boolean
  timeout?: number
}) => {
  const interval = useRef<ReturnType<typeof setInterval>>()
  const { ellisIslandClientId } = useSiteAssets()

  const needsRefresh = useCallback(() => {
    const { token, refreshToken } = getPersistedAuth()

    if (!refreshToken) return false

    const decodedToken = jwtDecode(token)
    const expiration = decodedToken.exp as number
    const tenMinutesFromNow = Date.now() / 1000 + 600
    // Forever tokens should be replaced. If the expiration
    // is more than a couple days in the future, it is a forever token
    const twoDaysFromNow = Date.now() / 1000 + 48 * 3600

    return expiration < tenMinutesFromNow || expiration > twoDaysFromNow
  }, [])

  const handleRefresh = useCallback(async () => {
    const auth = getPersistedAuth()
    const { accessToken, refreshToken } = await refreshAuthToken(
      auth.refreshToken,
      ellisIslandClientId
    )
    const decodedAuthToken = jwtDecode(accessToken)
    setPersistedAuth(decodedAuthToken.sub as string, accessToken, refreshToken)
  }, [ellisIslandClientId])

  useEffect(() => {
    if (!enabled) {
      if (interval.current) clearInterval(interval.current)
      return
    }

    const onInterval = async () => {
      try {
        if (needsRefresh()) await handleRefresh()
      } catch (err) {
        const auth = getPersistedAuth()
        const error: NotifiableError = new Error(
          `Error refreshing auth token: ${(err as Error)?.message}`,
          { cause: err }
        )
        report.error(error, { token: auth?.refreshToken })
      }
    }

    // Run first time immediately, then on interval
    onInterval()
    interval.current = setInterval(onInterval, timeout)

    return () => {
      if (interval.current) clearInterval(interval.current)
    }
  }, [needsRefresh, handleRefresh, enabled, timeout])
}

export default useAuthTokenRefresher
